import { inject } from "@angular/core";
import { patchState, signalStore, withComputed, withMethods, withProps, withState } from "@ngrx/signals";
import { s25Resource } from "../../../util/s25.resource";
import { SeOrgSecurityStore } from "../se.org.security.store";
import { StudentEngagementService } from "../student.engagement.service";
import { DataIngestionService, IngestionPoint } from "../../data-ingestion/data.ingestion.service";
import { IngestionPointCategory, IngestionPointFormUseKey } from "../../data-ingestion/data.ingestion.const";
import { IngestionAccess } from "../../data-ingestion/ingestion.access";
import { S25Util } from "../../../util/s25-util";
import { S25Assert } from "../../../util/s25.assert";
import { CacheRepository } from "../../../decorators/cache.decorator";
import { LoginStore } from "../../../stores/login.store";
import type { StudentEngagementOrg } from "../StudentEngagementOrg";
import { SeSecurityStore } from "../se.security.store";
import { SeSearchService } from "../search/se.search.service";

export const ORG_PAGE_FORM_USE_KEYS = [
    "orgEdit",
    "eventCreation",
    "orgJoin",
] as const satisfies readonly IngestionPointFormUseKey[];
export type OrgPageFormUseKey = (typeof ORG_PAGE_FORM_USE_KEYS)[number];

export function isOrgPageFormUseKey(useKey: unknown): useKey is OrgPageFormUseKey {
    return ORG_PAGE_FORM_USE_KEYS.includes(useKey as OrgPageFormUseKey);
}

function enabledActiveForms(forms: IngestionPoint[] | undefined): IngestionPoint[] {
    return (forms ?? []).filter((form) => form.enabled && !form.deleted && form.active !== false);
}

export type OrgAttributeWithName = StudentEngagementOrg.NamedAttribute;

interface SeOrgDetailsState {
    editMode: boolean;
    /** Incremented on every explicit reload so dependents can refetch. */
    detailsVersion: number;
    portalAccessDenied: boolean;
    /** Org id for which the next details load should skip the portal tile gate (explicit reload only). */
    skipPortalTileGateForOrgId: number | undefined;
}

export const SeOrgDetailsStore = signalStore(
    withState<SeOrgDetailsState>({
        editMode: false,
        detailsVersion: 0,
        portalAccessDenied: false,
        skipPortalTileGateForOrgId: undefined,
    }),
    withProps(() => ({
        _orgSecStore: inject(SeOrgSecurityStore),
        _loginStore: inject(LoginStore),
        _securityStore: inject(SeSecurityStore),
    })),
    withMethods((store) => ({
        setEditMode: (editMode: boolean) => patchState(store, { editMode }),
    })),
    withProps((store) => ({
        _orgDetailsResource: s25Resource({
            params: () => {
                const orgId = store._orgSecStore.orgId();
                if (!orgId) return undefined;
                return orgId;
            },
            loader: async ({ params: orgId, abortSignal }) => {
                patchState(store, { portalAccessDenied: false });

                const skipPortalTileGate = store.skipPortalTileGateForOrgId() === orgId;
                if (skipPortalTileGate) {
                    patchState(store, { skipPortalTileGateForOrgId: undefined });
                }

                // Post-reload refetches skip tile search — user is already on the org page (e.g. after join).
                const runPortalTileGate = !store._securityStore.hasFLSOverride() && !skipPortalTileGate;
                if (runPortalTileGate) {
                    const allowed = await SeSearchService.isConnect25OrganizationVisibleInPortalTile(orgId);
                    if (abortSignal.aborted) return undefined;
                    if (!allowed) {
                        patchState(store, { portalAccessDenied: true });
                        return undefined;
                    }
                }

                const details = await StudentEngagementService.getOrganizationDetails(orgId);
                if (abortSignal.aborted) return undefined;
                return details;
            },
        }),
        _orgScopedFormsResource: s25Resource({
            if: () => store._loginStore.loggedIn(),
            params: () => {
                const orgId = store._orgSecStore.orgId();
                if (!orgId) return undefined;
                return orgId;
            },
            loader: async ({ params: orgId }) => {
                const [forms, error] = await S25Util.Maybe(
                    DataIngestionService.getIngestionPoints(
                        IngestionPointCategory.ENGAGEMENT,
                        String(orgId),
                        undefined,
                        undefined,
                        IngestionAccess.IngestionPointAction.FILL,
                    ),
                );
                if (error) console.warn(S25Util.errorText(error));
                return forms ?? [];
            },
        }),
    })),
    withComputed((store) => ({
        orgScopedForms: () => enabledActiveForms(store._orgScopedFormsResource.value()),
    })),
    withProps((store) => ({
        _globalFormsResource: s25Resource({
            if: () => store._loginStore.loggedIn(),
            params: () => {
                if (!store._orgScopedFormsResource.value()) return undefined;
                const scopedForms = store.orgScopedForms();
                const missingUseKeys = ORG_PAGE_FORM_USE_KEYS.filter(
                    (useKey) => !scopedForms.some((form) => form.use?.[useKey]),
                );
                return missingUseKeys.length ? missingUseKeys : undefined;
            },
            loader: async ({ params: useKeys }) => {
                const [forms, error] = await S25Util.Maybe(
                    DataIngestionService.getIngestionPoints(
                        IngestionPointCategory.ENGAGEMENT,
                        undefined,
                        false,
                        useKeys,
                        IngestionAccess.IngestionPointAction.FILL,
                    ),
                );
                if (error) console.warn(S25Util.errorText(error));
                return forms ?? [];
            },
        }),
    })),
    withComputed((store) => ({
        _globalForms: () => enabledActiveForms(store._globalFormsResource.value()),
    })),
    withMethods((store) => ({
        formsForUse: (useKey: OrgPageFormUseKey): IngestionPoint[] => {
            const scopedForms = store.orgScopedForms().filter((form) => form.use?.[useKey]);
            if (scopedForms.length) return scopedForms;
            return store._globalForms().filter((form) => form.use?.[useKey]);
        },
    })),
    withComputed((store) => ({
        orgId: () => store._orgSecStore.orgId(),
        orgDetails: () => store._orgDetailsResource.value(),
        /** The last details returned by a load, ignoring local {@link patchDetails} changes. */
        loadedDetails: () => store._orgDetailsResource.lastValue(),
        isLoading: () => store._orgDetailsResource.resource.isLoading(),
        attributes: (): OrgAttributeWithName[] => store._orgDetailsResource.value()?.attributes ?? [],
        name: () => store._orgDetailsResource.value()?.name,
        mission: () => store._orgDetailsResource.value()?.mission,
        imageAttr: () => store._orgDetailsResource.value()?.imageAttr,
        orgRating: () => store._orgDetailsResource.value()?.orgRating,
        primaryContact: () => store._orgDetailsResource.value()?.primaryContact,
        members: () => store._orgDetailsResource.value()?.members,
        upcomingEvents: () => store._orgDetailsResource.value()?.upcomingEvents,
        isCurrentUserOrgMember: () => {
            if (!store._loginStore.loggedIn()) return false;
            const userId = store._loginStore.user()?.userId;
            const members = store._orgDetailsResource.value()?.members?.() ?? [];
            return members.some((member) => member.itemId === userId);
        },
    })),
    withMethods((store) => ({
        canUseForm: (useKey: OrgPageFormUseKey): boolean => {
            switch (useKey) {
                case "orgJoin":
                    return store._loginStore.loggedIn() && !store.isCurrentUserOrgMember();
                case "eventCreation":
                    return !!store._orgSecStore.canCreateEvent();
                case "orgEdit":
                    return store._loginStore.loggedIn();
                default:
                    S25Assert.never(useKey, "Unhandled org page form use");
            }
        },
    })),
    withMethods((store) => ({
        reloadDetails: () => {
            CacheRepository.invalidateByService("OrganizationService.getOrganizationsById");
            patchState(store, {
                detailsVersion: store.detailsVersion() + 1,
                skipPortalTileGateForOrgId: store._orgSecStore.orgId(),
            });
            return store._orgDetailsResource.reloadAndAwait();
        },
        patchDetails: (updates: Partial<StudentEngagementOrg.details>) => {
            store._orgDetailsResource.resource.value.update((details) => details && { ...details, ...updates });
        },
    })),
);
